import { useEffect, useRef, useState, useSyncExternalStore } from 'react';

import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/primitives/Dialog';
import { config } from '@/config';
import { useTranslation } from '@/hooks/useTranslation';
import { captchaRequests, loadCaptchaSdk, type CaptchaRequest } from '@/modules/auth/services/waf';

// The CAPTCHA SDK's locale for each app language.
const CAPTCHA_LOCALES: Record<string, string> = { en: 'en-US', ar: 'ar-SA', id: 'id-ID' };

/**
 * Shows the AWS WAF CAPTCHA when a bot rule asks for one (see `wafProtected`). Mount it once on
 * any page whose requests go through the WAF's bot checks.
 */
export function WafCaptchaDialog() {
  const { t, language } = useTranslation();
  const request = useSyncExternalStore(captchaRequests.subscribe, captchaRequests.current);
  const [container, setContainer] = useState<HTMLDivElement | null>(null);
  const [failed, setFailed] = useState(false);
  const renderedFor = useRef<CaptchaRequest | null>(null);

  useEffect(() => () => captchaRequests.current()?.cancel(), []);

  useEffect(() => {
    // One puzzle per request, even when a new request replaces an open one.
    if (!request || !container || renderedFor.current === request) return;
    renderedFor.current = request;
    container.replaceChildren();
    setFailed(false);
    void loadCaptchaSdk().then((captcha) => {
      if (!captcha) {
        setFailed(true);
        return;
      }
      captcha.renderCaptcha(container, {
        apiKey: config.waf.captchaApiKey,
        onSuccess: (token) => request.solve(token),
        onError: (error) => {
          console.error('[waf] CAPTCHA error', error);
          setFailed(true);
        },
        dynamicWidth: true,
        skipTitle: true,
        defaultLocale: CAPTCHA_LOCALES[language] ?? 'en-US',
      });
    });
  }, [request, container, language]);

  return (
    <Dialog open={!!request} onOpenChange={(open) => !open && request?.cancel()}>
      <DialogContent closeLabel={t('Close')}>
        <DialogHeader>
          <DialogTitle>{t('Confirm you are human')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <DialogDescription>{t('Solve this quick check to continue.')}</DialogDescription>
          <div ref={setContainer} />
          {failed && (
            <p role="alert" className="text-sm text-status-danger">
              {t('The security check could not load. Reload the page and try again.')}
            </p>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
